import { useState } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableRow as MuiTableRow,
  TableBody,
  TableContainer,
  TableFooter,
  Box,
  Grid,
  TableCell as MuiTableCell,
} from "@mui/material";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { Typography } from "@platform/design-system";

import { TablePagination } from "../../components";

import { Carrier, Columns, Header } from "./types";
import SortableRow from "./sortableRow";
import TableCell from "./tableCell";
import { getHeaders } from "./helpers";

type Props = {
  id: Columns;
  title: string;
  carriers: Carrier[];
  showFooter?: boolean;
  showIndex?: boolean;
};

function CarriersTable({
  id,
  title,
  carriers,
  showFooter = true,
  showIndex = true,
}: Props): React.JSX.Element {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const headers: Header[] = getHeaders(showIndex);

  const offset = page * rowsPerPage;
  const limit = offset + rowsPerPage;
  const displayCarriers = carriers.slice(offset, limit);
  const carrierIds = displayCarriers.map((c) => c.id);
  const { setNodeRef } = useDroppable({
    id,
    data: { column: id },
  });

  return (
    <Grid size={{ xs: 12, lg: 6 }}>
      <Typography variant="header-medium" mb={3}>
        {title}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <MuiTableRow>
              {headers.map((h) => (
                <MuiTableCell key={h.id}>{h.label}</MuiTableCell>
              ))}
            </MuiTableRow>
          </TableHead>
          <>
            <TableBody>
              <SortableContext
                items={carrierIds}
                strategy={verticalListSortingStrategy}
              >
                {displayCarriers.map((carrier, index) => (
                  <SortableRow
                    key={carrier.id}
                    column={id}
                    carrier={carrier}
                    index={index}
                    showIndex={showIndex}
                  />
                ))}
              </SortableContext>
              {carriers?.length < 1 && (
                <MuiTableRow ref={setNodeRef}>
                  <TableCell colSpan={2}>
                    <Box
                      component="span"
                      sx={{
                        display: "inline",
                        padding: 0,
                        color: "text.secondary",
                      }}
                    >
                      No selected carriers
                    </Box>
                  </TableCell>
                </MuiTableRow>
              )}
            </TableBody>
            {showFooter && carriers?.length > 0 && (
              <TableFooter>
                <MuiTableRow>
                  <TablePagination
                    count={carriers.length}
                    page={page}
                    setPage={setPage}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    rowsPerPageOptions={[20, 40, 60]}
                  />
                </MuiTableRow>
              </TableFooter>
            )}
          </>
        </Table>
      </TableContainer>
    </Grid>
  );
}

export default CarriersTable;
